import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, FileImage, X, CheckCircle, AlertCircle, Loader2, Building2 } from 'lucide-react';
import React, { useRef, useState, useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { uploadApi, uploadToS3, assetsApi, facilitiesApi, queryKeys } from '@/lib/api';
import { useAuthStore } from '@/store/auth';

interface UploadProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  key?: string;
}

export default function UploaderPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [assets, setAssets] = useState<any[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(true);
  const [selectedFacility, setSelectedFacility] = useState<string>('');

  // Fetch facilities for admin users
  const { data: facilitiesData } = useQuery({
    queryKey: queryKeys.facilities.list(),
    queryFn: () => facilitiesApi.list({ limit: 100 }),
    enabled: user?.role === 'admin',
  });

  const facilities = facilitiesData?.facilities || [];
  const activeFacilities = facilities
    .filter(f => f.isActive)
    .sort((a, b) => a.name.localeCompare(b.name));

  const validateFile = async (file: File): Promise<{ valid: boolean; error?: string }> => {
    // Check file type - PNG only
    if (file.type !== 'image/png') {
      return { valid: false, error: 'File must be PNG format' };
    }
    
    // Check file size - max 500KB
    const maxSize = 500 * 1024; // 500KB
    if (file.size > maxSize) {
      return { valid: false, error: 'File size must not exceed 500KB' };
    }
    
    // Check dimensions - must be 860x200
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (img.width !== 860 || img.height !== 200) {
          resolve({ valid: false, error: 'Image must be exactly 860x200 pixels' });
        } else {
          resolve({ valid: true });
        }
      };
      img.onerror = () => {
        resolve({ valid: false, error: 'Invalid image file' });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files) return;
    
    const validFiles: File[] = [];
    const errors: string[] = [];
    
    for (const file of Array.from(files)) {
      const validation = await validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    }
    
    if (errors.length > 0) {
      alert(`Some files were rejected:\n${errors.join('\n')}`);
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))  } ${  sizes[i]}`;
  };

  // Load assets on component mount and when facility changes
  useEffect(() => {
    loadAssets();
  }, [selectedFacility]);

  const loadAssets = async () => {
    try {
      setLoadingAssets(true);
      const params: any = { limit: 100, assetType: 'slideshow' };
      if (user?.role === 'admin' && selectedFacility) {
        params.facilityId = selectedFacility;
      }
      const response = await assetsApi.list(params);
      setAssets([...response.assets].sort((a, b) => a.originalName.localeCompare(b.originalName)));
    } catch (error) {
      console.error('Failed to load assets:', error);
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleUpload = async () => {
    console.log('Upload started with files:', selectedFiles.length);
    if (selectedFiles.length === 0) return;

    // Validate facility selection for admin users
    if (user?.role === 'admin' && !selectedFacility) {
      alert('Please select a facility before uploading.');
      return;
    }

    setIsUploading(true);
    const progress: UploadProgress[] = selectedFiles.map(file => ({
      file,
      progress: 0,
      status: 'pending' as const,
    }));
    setUploadProgress(progress);

    const successfulUploads: Array<{
      key: string;
      filename: string;
      originalName: string;
      contentType: string;
      size: number;
      width?: number;
      height?: number;
      assetType?: 'slideshow' | 'logo';
    }> = [];

    // Process each file
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      if (!file) continue;
      
      try {
        // Update status to uploading
        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { ...p, status: 'uploading' } : p
        ));

        // Get image dimensions
        const dimensions = await new Promise<{ width: number; height: number }>((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ width: img.width, height: img.height });
          img.src = URL.createObjectURL(file);
        });

        // Get presigned upload URL
        console.log(`Getting presign URL for ${file.name}`);
        const presignParams: any = {
          filename: file.name,
          contentType: file.type,
          size: file.size,
          assetType: 'slideshow',
        };

        if (user?.role === 'admin' && selectedFacility) {
          presignParams.facilityId = selectedFacility;
        }
        
        const presignResponse = await uploadApi.presign(presignParams);
        console.log('Presign response:', presignResponse);

        // Upload to S3
        await uploadToS3(
          { url: presignResponse.uploadUrl, fields: presignResponse.fields },
          file,
          (progressPercent) => {
            setUploadProgress(prev => prev.map((p, idx) => 
              idx === i ? { ...p, progress: progressPercent } : p
            ));
          }
        );

        // Mark as successful
        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { ...p, status: 'success', key: presignResponse.key } : p
        ));

        // Add to successful uploads
        successfulUploads.push({
          key: presignResponse.key,
          filename: file.name,
          originalName: file.name,
          contentType: file.type,
          size: file.size,
          width: dimensions.width,
          height: dimensions.height,
          assetType: 'slideshow' as const,
        });

      } catch (error) {
        console.error(`Upload failed for ${file.name}:`, error);
        console.error('Full error object:', error);
        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { 
            ...p, 
            status: 'error', 
            error: error instanceof Error ? error.message : 'Upload failed' 
          } : p
        ));
      }
    }

    // Notify server of successful uploads
    if (successfulUploads.length > 0) {
      try {
        const notifyParams: any = {
          uploads: successfulUploads,
        };
        
        // Add facilityId for admin users
        if (user?.role === 'admin' && selectedFacility) {
          notifyParams.facilityId = selectedFacility;
        }
        
        await uploadApi.notify(notifyParams);

        // Reload assets and invalidate caches so other pages see the new uploads
        await loadAssets();
        queryClient.invalidateQueries({ queryKey: ['assets'] });
        queryClient.invalidateQueries({ queryKey: ['admin-assets'] });
        queryClient.invalidateQueries({ queryKey: ['facility-assets'] });
        
        // Clear selected files
        setSelectedFiles([]);
        
        // Clear upload progress after a delay
        setTimeout(() => {
          setUploadProgress([]);
        }, 3000);
        
      } catch (error) {
        console.error('Failed to notify server:', error);
      }
    }

    setIsUploading(false);
  };

  const deleteAsset = async (assetId: number) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;
    
    try {
      await assetsApi.delete(assetId);
      await loadAssets();
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['admin-assets'] });
      queryClient.invalidateQueries({ queryKey: ['facility-assets'] });
    } catch (error) {
      console.error('Failed to delete asset:', error);
      alert('Failed to delete asset. Please try again.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Slideshow Uploader</h1>
        <p className="text-muted-foreground">Upload sponsor banners for the Slideshow widget — PNG, 860×200px, max 500 KB</p>
      </div>

      {/* Facility Selector for Admin Users */}
      {user?.role === 'admin' && (
        <Card className="mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Facility Selection
            </CardTitle>
            <CardDescription>
              Select which facility to upload assets for
            </CardDescription>
          </CardHeader>
          <CardContent>
            <select
              value={selectedFacility}
              onChange={(e) => setSelectedFacility(e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-md bg-background"
            >
              <option value="">Select a facility...</option>
              {activeFacilities.map((facility) => (
                <option key={facility.facilityId} value={facility.facilityId}>
                  {facility.name} ({facility.facilityId})
                </option>
              ))}
            </select>
            {!selectedFacility && (
              <p className="text-sm text-muted-foreground mt-2">
                You must select a facility before uploading files
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Upload Area */}
        <Card>
          <CardHeader>
            <CardTitle>Upload Assets</CardTitle>
            <CardDescription>
              Drag and drop files or click to browse
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div 
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-primary bg-primary/5' 
                  : 'border-muted-foreground/25'
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium mb-2">Drop files here</p>
              <p className="text-sm text-muted-foreground mb-4">
                PNG files only • 860x200 pixels • Max 500KB
              </p>
              
              {/* Template Preview */}
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Required Dimensions:</p>
                <div
                  className="mx-auto border-2 border-dashed border-gray-400 bg-white dark:bg-gray-700 dark:border-gray-600 flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm"
                  style={{ width: '215px', height: '50px' }}
                >
                  860 x 200 pixels
                </div>
                <p className="text-xs text-muted-foreground mt-1">(Shown at 25% scale)</p>
              </div>

              <div className="flex flex-col gap-2 mb-4">
                <Button onClick={handleBrowseClick}>
                  Browse Files
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    // Generate and download template
                    const canvas = document.createElement('canvas');
                    canvas.width = 860;
                    canvas.height = 200;
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                      ctx.fillStyle = '#ffffff';
                      ctx.fillRect(0, 0, 860, 200);
                      
                      const link = document.createElement('a');
                      link.download = 'template-860x200.png';
                      link.href = canvas.toDataURL();
                      link.click();
                    }
                  }}
                >
                  Download Template (860x200.png)
                </Button>
              </div>
              
              <div className="text-xs text-muted-foreground bg-blue-50 p-3 rounded border">
                <p className="font-medium text-blue-900 mb-1">💡 How to use the template:</p>
                <p className="text-blue-700">1. Download the white background template above</p>
                <p className="text-blue-700">2. Open it in your favorite image editor (Photoshop, GIMP, Canva, etc.)</p>
                <p className="text-blue-700">3. Add your logo and text while keeping the 860x200 size</p>
                <p className="text-blue-700">4. Save as PNG and upload here</p>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/png"
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
              />
            </div>

            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium mb-3">Selected Files ({selectedFiles.length})</h3>
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileImage className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{file.name}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex space-x-2">
                  <Button 
                    className="flex-1" 
                    onClick={handleUpload}
                    disabled={isUploading || (user?.role === 'admin' && !selectedFacility)}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setSelectedFiles([])} disabled={isUploading}>
                    Clear All
                  </Button>
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {uploadProgress.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium mb-3">Upload Progress</h3>
                <div className="space-y-3">
                  {uploadProgress.map((progress, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {progress.status === 'success' && <CheckCircle className="h-4 w-4 text-green-500" />}
                          {progress.status === 'error' && <AlertCircle className="h-4 w-4 text-red-500" />}
                          {progress.status === 'uploading' && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
                          {progress.status === 'pending' && <FileImage className="h-4 w-4 text-muted-foreground" />}
                          <span className="text-sm font-medium">{progress.file.name}</span>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {progress.status === 'success' && 'Complete'}
                          {progress.status === 'error' && 'Failed'}
                          {progress.status === 'uploading' && `${progress.progress}%`}
                          {progress.status === 'pending' && 'Waiting'}
                        </span>
                      </div>
                      {progress.status === 'uploading' && (
                        <div className="w-full bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${progress.progress}%` }}
                          />
                        </div>
                      )}
                      {progress.error && (
                        <p className="text-sm text-red-500">{progress.error}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Current Assets */}
        <Card>
          <CardHeader>
            <CardTitle>
              Current Assets
              {user?.role === 'admin' && selectedFacility && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  for {activeFacilities.find(f => f.facilityId === selectedFacility)?.name}
                </span>
              )}
            </CardTitle>
            <CardDescription>
              {user?.role === 'admin' && !selectedFacility 
                ? 'Select a facility to view assets'
                : 'Your uploaded logos and assets'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingAssets ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 mx-auto mb-4 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground">Loading assets...</p>
              </div>
            ) : assets.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No assets uploaded yet</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Upload your first logo to get started
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {assets.map((asset) => (
                  <div key={asset.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-8 bg-muted rounded overflow-hidden">
                        <img 
                          src={asset.s3Url} 
                          alt={asset.originalName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{asset.originalName}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(asset.fileSize)} • {asset.width}x{asset.height}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Uploaded {new Date(asset.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteAsset(asset.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
